import React, { useState, useEffect, useCallback } from 'react';

const ADMIN_CSS = `
  .admin-card { animation: fadeUp 0.35s ease both; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
  .admin-btn:hover:not(:disabled) { opacity: 0.85; transform: translateY(-1px); }
  .admin-btn { transition: all 0.15s; }
`;

const API = process.env.REACT_APP_API_URL || '';

export default function AdminDashboard({ onBack }) {
  const [password, setPassword]   = useState(() => localStorage.getItem('dd_admin_pwd') || '');
  const [authed, setAuthed]       = useState(false);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [error, setError]         = useState('');

  const fetchStats = useCallback(async (pwd) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/stats`, {
        headers: { 'x-admin-password': pwd },
      });
      if (res.status === 401) { setError('Wrong password.'); setAuthed(false); return; }
      const data = await res.json();
      setStats(data);
      setAuthed(true);
      localStorage.setItem('dd_admin_pwd', pwd);
    } catch {
      setError('Could not reach server.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (password) fetchStats(password);
  }, []); // eslint-disable-line

  async function handleAction(endpoint, label) {
    setActionMsg('');
    try {
      const res = await fetch(`${API}/api/admin/${endpoint}`, {
        method: 'POST',
        headers: { 'x-admin-password': password },
      });
      const data = await res.json();
      setActionMsg(data.message || 'Done.');
      setTimeout(() => setActionMsg(''), 4000);
    } catch {
      setActionMsg('Failed.');
    }
  }

  /* ── Login ── */
  if (!authed) {
    return (
      <>
        <style>{ADMIN_CSS}</style>
        <div style={s.page}>
          <div style={s.orb1} /><div style={s.orb2} />
          <div className="admin-card" style={s.loginCard}>
            <button onClick={onBack} style={s.backBtn}>← Back</button>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔐</div>
            <h1 style={s.title}>Admin Dashboard</h1>
            <p style={s.sub}>Enter your admin password to continue.</p>
            <input
              type="password"
              placeholder="Admin password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchStats(password)}
              style={s.input}
              autoFocus
            />
            {error && <div style={s.errorBox}>{error}</div>}
            <button
              className="admin-btn"
              onClick={() => fetchStats(password)}
              disabled={loading || !password}
              style={{ ...s.btn, opacity: loading || !password ? 0.4 : 1 }}
            >
              {loading ? 'Checking…' : 'Access dashboard →'}
            </button>
          </div>
        </div>
      </>
    );
  }

  /* ── Dashboard ── */
  return (
    <>
      <style>{ADMIN_CSS}</style>
      <div style={s.dashPage}>
        <div style={s.orb1} /><div style={s.orb2} />

        {/* Header */}
        <div style={s.header}>
          <div style={s.headerLeft}>
            <span style={{ fontSize: 24 }}>💧</span>
            <div>
              <div style={s.headerTitle}>DailyDroplet Admin</div>
              <div style={s.headerSub}>Operations dashboard</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {actionMsg && <span style={s.actionMsg}>{actionMsg}</span>}
            <button onClick={() => fetchStats(password)} style={s.refreshBtn} disabled={loading}>
              {loading ? '⟳' : '↺ Refresh'}
            </button>
            <button onClick={onBack} style={s.logoutBtn}>← Site</button>
          </div>
        </div>

        <div style={s.body}>

          {/* Stat cards */}
          <div style={s.statRow}>
            <StatCard icon="👥" label="Total Subscribers" value={stats?.totalUsers ?? '—'} color="#0ea5e9" />
            <StatCard icon="📦" label="Cached Articles" value={stats?.cachedItems ?? '—'} color="#6366f1" />
            <StatCard icon="🏷️" label="Unique Topics" value={stats?.topTopics?.length ?? '—'} color="#22c55e" />
            <StatCard icon="📡" label="Sources Active" value="9" color="#f59e0b" />
          </div>

          {/* Actions */}
          <div className="admin-card" style={s.section}>
            <h2 style={s.sectionTitle}>⚡ Actions</h2>
            <div style={s.actionRow}>
              <ActionButton
                icon="📬" label="Send Digests Now"
                sub="Triggers digest for all users"
                color="#0ea5e9"
                onClick={() => handleAction('send-digests', 'Send Digests')}
              />
              <ActionButton
                icon="🔄" label="Refresh Content Cache"
                sub="Pulls fresh content from all APIs"
                color="#6366f1"
                onClick={() => handleAction('refresh-cache', 'Refresh Cache')}
              />
            </div>
          </div>

          <div style={s.twoCol}>
            {/* Top topics */}
            <div className="admin-card" style={s.section}>
              <h2 style={s.sectionTitle}>🏆 Top Topics</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(stats?.topTopics || []).map((t, i) => {
                  const max = stats.topTopics[0]?.count || 1;
                  return (
                    <div key={t.topic}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, color: '#94a3b8' }}>
                          <span style={{ color: '#475569', marginRight: 8 }}>#{i + 1}</span>{t.topic}
                        </span>
                        <span style={{ fontSize: 13, color: '#38bdf8', fontWeight: 700 }}>{t.count}</span>
                      </div>
                      <div style={{ height: 4, background: '#0d1829', borderRadius: 2 }}>
                        <div style={{
                          height: '100%', borderRadius: 2,
                          width: `${(t.count / max) * 100}%`,
                          background: 'linear-gradient(90deg, #0ea5e9, #6366f1)',
                          transition: 'width 0.6s ease',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent signups */}
            <div className="admin-card" style={s.section}>
              <h2 style={s.sectionTitle}>🆕 Recent Signups</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {(stats?.recentUsers || []).map((u, i) => (
                  <UserRow key={i} user={u} password={password} />
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

function UserRow({ user: u, password }) {
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const [msg, setMsg]     = useState('');

  async function sendToUser() {
    setState('sending');
    try {
      const res = await fetch(`${API}/api/admin/send-digest-to-user`, {
        method: 'POST',
        headers: { 'x-admin-password': password, 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: u.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setState('sent');
      setMsg(data.message || 'Sent!');
      setTimeout(() => { setState('idle'); setMsg(''); }, 4000);
    } catch (err) {
      setState('error');
      setMsg(err.message);
      setTimeout(() => { setState('idle'); setMsg(''); }, 5000);
    }
  }

  const btnColor = state === 'sent' ? '#22c55e' : state === 'error' ? '#ef4444' : '#0ea5e9';
  const btnLabel = state === 'sending' ? '⟳' : state === 'sent' ? '✓' : state === 'error' ? '!' : '📬';

  return (
    <div style={{ ...s.userRow, flexWrap: 'wrap', gap: 8 }}>
      <div style={s.userAvatar}>{(u.name || '?')[0].toUpperCase()}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={s.userName}>{u.name || 'Anonymous'}</div>
        <div style={s.userMeta}>{u.contact} · {u.delivery_time} {u.timezone?.split('/')[1]?.replace('_', ' ') || ''}</div>
        {msg && <div style={{ fontSize: 11, color: btnColor, marginTop: 2 }}>{msg}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <div style={s.userDate}>{new Date(u.joined).toLocaleDateString()}</div>
        <button
          className="admin-btn"
          onClick={sendToUser}
          disabled={state === 'sending'}
          title="Send digest to this user"
          style={{
            background: btnColor + '18',
            border: `1px solid ${btnColor}44`,
            borderRadius: 8,
            padding: '5px 10px',
            cursor: state === 'sending' ? 'not-allowed' : 'pointer',
            fontSize: 14,
            color: btnColor,
            fontFamily: 'inherit',
            opacity: state === 'sending' ? 0.5 : 1,
          }}
        >
          {btnLabel}
        </button>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="admin-card" style={{ ...s.statCard, borderColor: color + '33' }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 32, fontWeight: 800, color, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    </div>
  );
}

function ActionButton({ icon, label, sub, color, onClick }) {
  const [running, setRunning] = useState(false);
  async function handle() {
    setRunning(true);
    await onClick();
    setRunning(false);
  }
  return (
    <button className="admin-btn" onClick={handle} disabled={running} style={{
      background: color + '11', border: `1px solid ${color}33`,
      borderRadius: 14, padding: '20px 24px', cursor: 'pointer',
      fontFamily: 'inherit', textAlign: 'left', flex: 1,
      opacity: running ? 0.5 : 1,
    }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>{running ? '⟳' : icon}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>{running ? 'Running…' : label}</div>
      <div style={{ fontSize: 12, color: '#475569' }}>{sub}</div>
    </button>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#060d1f', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: "'Inter', system-ui, sans-serif", position: 'relative' },
  dashPage: { minHeight: '100vh', background: '#060d1f', fontFamily: "'Inter', system-ui, sans-serif", color: '#f1f5f9', position: 'relative' },
  orb1: { position: 'fixed', top: '5%', left: '5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle,rgba(14,165,233,0.07) 0%,transparent 70%)', filter: 'blur(30px)', pointerEvents: 'none', zIndex: 0 },
  orb2: { position: 'fixed', bottom: '10%', right: '5%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle,rgba(99,102,241,0.06) 0%,transparent 70%)', filter: 'blur(30px)', pointerEvents: 'none', zIndex: 0 },
  loginCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid #1e293b', borderRadius: 24, padding: 40, maxWidth: 400, width: '100%', position: 'relative', zIndex: 1, boxShadow: '0 40px 80px rgba(0,0,0,0.4)' },
  backBtn: { background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, padding: '0 0 16px 0', display: 'block' },
  title: { fontSize: 28, fontWeight: 800, color: '#f8fafc', marginBottom: 8 },
  sub: { fontSize: 14, color: '#475569', marginBottom: 24, lineHeight: 1.6 },
  input: { width: '100%', padding: '13px 16px', fontSize: 15, background: '#0a1628', border: '1.5px solid #1e3a5f', borderRadius: 12, fontFamily: 'inherit', color: '#f1f5f9', marginBottom: 16, boxSizing: 'border-box' },
  btn: { width: '100%', padding: 14, fontSize: 15, fontWeight: 700, background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', color: '#fff', border: 'none', borderRadius: 12, fontFamily: 'inherit', cursor: 'pointer' },
  errorBox: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 14px', color: '#f87171', fontSize: 13, marginBottom: 14 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 32px', borderBottom: '1px solid #0d1829', background: 'rgba(6,13,31,0.9)', backdropFilter: 'blur(16px)', position: 'sticky', top: 0, zIndex: 100 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  headerTitle: { fontSize: 16, fontWeight: 800, color: '#f8fafc' },
  headerSub: { fontSize: 11, color: '#334155' },
  refreshBtn: { background: 'none', border: '1px solid #1e3a5f', color: '#475569', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12 },
  logoutBtn: { background: 'none', border: '1px solid #1e3a5f', color: '#475569', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12 },
  actionMsg: { fontSize: 13, color: '#22c55e', fontWeight: 600 },
  body: { maxWidth: 1100, margin: '0 auto', padding: 'clamp(24px,4vw,40px) clamp(16px,4vw,32px) 60px', position: 'relative', zIndex: 1 },
  statRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 },
  statCard: { background: '#0a1628', border: '1px solid', borderRadius: 16, padding: '24px 20px', textAlign: 'center' },
  section: { background: 'rgba(255,255,255,0.02)', border: '1px solid #1e293b', borderRadius: 16, padding: 24, marginBottom: 24 },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 20 },
  actionRow: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 24 },
  userRow: { display: 'flex', alignItems: 'center', gap: 12 },
  userAvatar: { width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: '#fff', flexShrink: 0 },
  userName: { fontSize: 14, fontWeight: 600, color: '#f1f5f9', marginBottom: 2 },
  userMeta: { fontSize: 11, color: '#475569' },
  userDate: { fontSize: 11, color: '#334155', flexShrink: 0 },
};
